import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createAuthNonce,
  createAuthSession,
  getMaterialization,
  listMaterializations,
  saveMaterialization,
} from "../api/client";
import type {
  MaterializationDetail,
  MaterializationRequest,
  MaterializationSummary,
} from "../api/types";

type EthereumProvider = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
};

declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}

export type WalletSession = {
  wallet: string;
  token: string;
  expires_at: string;
};

const STORAGE_KEY = "clobsidian.walletSession";

const parseStoredSession = (): WalletSession | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WalletSession;
    if (!parsed.wallet || !parsed.token || !parsed.expires_at) return null;
    if (new Date(parsed.expires_at).getTime() <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const shortError = (error: unknown): string =>
  error instanceof Error ? error.message : "wallet request failed";

export const shortWallet = (wallet: string): string =>
  wallet.length <= 12 ? wallet : `${wallet.slice(0, 6)}…${wallet.slice(-4)}`;

export const useWalletSession = () => {
  const [session, setSession] = useState<WalletSession | null>(() =>
    parseStoredSession(),
  );
  const [connecting, setConnecting] = useState(false);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [readerLoading, setReaderLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readerError, setReaderError] = useState<string | null>(null);
  const [materializations, setMaterializations] = useState<
    MaterializationSummary[]
  >([]);
  const [selectedMaterialization, setSelectedMaterialization] =
    useState<MaterializationDetail | null>(null);

  const authenticated = useMemo(() => Boolean(session?.token), [session]);

  const persistSession = useCallback((next: WalletSession | null) => {
    setSession(next);
    if (next) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } else {
      localStorage.removeItem(STORAGE_KEY);
      setMaterializations([]);
      setSelectedMaterialization(null);
    }
  }, []);

  const refreshMaterializations = useCallback(async () => {
    if (!session?.token) return;
    setVaultLoading(true);
    setError(null);
    try {
      setMaterializations(await listMaterializations(session.token));
    } catch (err) {
      setError(shortError(err));
    } finally {
      setVaultLoading(false);
    }
  }, [session?.token]);

  useEffect(() => {
    refreshMaterializations();
  }, [refreshMaterializations]);

  const connect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      if (!window.ethereum) {
        throw new Error("no browser wallet found");
      }
      const accounts = (await window.ethereum.request({
        method: "eth_requestAccounts",
      })) as string[];
      const wallet = accounts[0];
      if (!wallet) throw new Error("no wallet account returned");

      const nonce = await createAuthNonce(wallet);
      const signature = (await window.ethereum.request({
        method: "personal_sign",
        params: [nonce.message, nonce.wallet],
      })) as string;
      const auth = await createAuthSession({
        wallet: nonce.wallet,
        nonce: nonce.nonce,
        signature,
      });
      persistSession(auth);
    } catch (err) {
      setError(shortError(err));
    } finally {
      setConnecting(false);
    }
  }, [persistSession]);

  const disconnect = useCallback(() => {
    setError(null);
    persistSession(null);
  }, [persistSession]);

  const saveToVault = useCallback(
    async (request: MaterializationRequest): Promise<MaterializationDetail | null> => {
      if (!session?.token) {
        setError("connect wallet first");
        return null;
      }
      setSaving(true);
      setError(null);
      try {
        const saved = await saveMaterialization(session.token, request);
        await refreshMaterializations();
        return saved;
      } catch (err) {
        setError(shortError(err));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [refreshMaterializations, session?.token],
  );

  const openMaterialization = useCallback(
    async (id: string) => {
      if (!session?.token) {
        setError("connect wallet first");
        return;
      }
      setReaderLoading(true);
      setReaderError(null);
      try {
        setSelectedMaterialization(await getMaterialization(session.token, id));
      } catch (err) {
        setReaderError(shortError(err));
      } finally {
        setReaderLoading(false);
      }
    },
    [session?.token],
  );

  const closeMaterialization = useCallback(() => {
    setReaderError(null);
    setSelectedMaterialization(null);
  }, []);

  return {
    authenticated,
    session,
    connecting,
    vaultLoading,
    saving,
    readerLoading,
    error,
    readerError,
    materializations,
    selectedMaterialization,
    connect,
    disconnect,
    refreshMaterializations,
    saveToVault,
    openMaterialization,
    closeMaterialization,
  };
};
